/**
 * StateServiceEvents.ts
 * Types d'événements émis par StateService via EventBus
 * Permet de découpler StateService des autres services
 */
import type { ShapeData } from '../types/ShapeTypes';
import type { PhysicalFormat, TargetBoothType } from './StateService';

/**
 * Types d'événements émis par StateService
 */
export enum StateServiceEventType {
  // Événements de shapes
  SHAPE_ADDED = 'state:shape:added',
  SHAPE_UPDATED = 'state:shape:updated',
  SHAPE_DELETED = 'state:shape:deleted',
  SHAPES_CLEARED = 'state:shapes:cleared',

  // Événements de sélection
  SELECTION_CHANGED = 'state:selection:changed',

  // Événements de projet
  PROJECT_DIMENSIONS_CHANGED = 'state:project:dimensions:changed',
  PROJECT_FORMAT_CHANGED = 'state:project:format:changed',

  // Événements de canvas
  CANVAS_PROPERTIES_CHANGED = 'state:canvas:properties:changed',
  CANVAS_ZOOM_CHANGED = 'state:canvas:zoom:changed',

  // Événements de calques
  LAYER_COUNT_CHANGED = 'state:layers:count:changed',

  // Événements d'UI
  UI_TOOL_CHANGED = 'state:ui:tool:changed',
  UI_DRAWER_CHANGED = 'state:ui:drawer:changed',
}

/**
 * Données pour l'événement SHAPE_ADDED
 */
export interface ShapeAddedEventData {
  shape: ShapeData;
}

/**
 * Données pour l'événement SHAPE_UPDATED
 */
export interface ShapeUpdatedEventData {
  shapeId: string;
  updates: Partial<ShapeData>;
}

/**
 * Données pour l'événement SHAPE_DELETED
 */
export interface ShapeDeletedEventData {
  shapeId: string;
}

/**
 * Données pour l'événement SELECTION_CHANGED
 */
export interface SelectionChangedEventData {
  selectedIds: string[];
  previousIds: string[];
}

/**
 * Données pour l'événement PROJECT_DIMENSIONS_CHANGED
 */
export interface ProjectDimensionsChangedEventData {
  width: number;
  height: number;
  previousWidth: number;
  previousHeight: number;
}

/**
 * Données pour l'événement PROJECT_FORMAT_CHANGED
 */
export interface ProjectFormatChangedEventData {
  format: PhysicalFormat;
  targetBoothType: TargetBoothType;
}

/**
 * Données pour l'événement CANVAS_PROPERTIES_CHANGED
 */
export interface CanvasPropertiesChangedEventData {
  properties: Record<string, unknown>;
}

/**
 * Données pour l'événement CANVAS_ZOOM_CHANGED
 */
export interface CanvasZoomChangedEventData {
  zoomLevel: number;
  previousZoomLevel: number;
}

/**
 * Données pour l'événement LAYER_COUNT_CHANGED
 */
export interface LayerCountChangedEventData {
  count: number;
  previousCount: number;
}

/**
 * Données pour l'événement UI_TOOL_CHANGED
 */
export interface UIToolChangedEventData {
  tool: string | null;
  previousTool: string | null;
}

/**
 * Données pour l'événement UI_DRAWER_CHANGED
 */
export interface UIDrawerChangedEventData {
  drawerType: string | null;
  isOpen: boolean;
}
